import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, UserCheck } from "lucide-react";
import { phoneDigits, formatTrPhone, isValidTrPhone } from "@/lib/utils";

// Google/e-posta ile giriş sonrası profili eksik (ad soyad veya telefon yok) olan
// kullanıcıdan bu bilgileri toplar. Admin "Kullanıcılar" listesinde görünür.
export const ProfileCompletionDialog = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) { setOpen(false); return; }

    // YALNIZCA Google ile kayıt olan kullanıcıya sor. E-posta/şifre kaydında zaten alınıyor.
    const am = (user.app_metadata || {}) as Record<string, any>;
    const isGoogle = am.provider === "google" || (Array.isArray(am.providers) && am.providers.includes("google"));
    if (!isGoogle) return;

    // Bir kez tamamlayan bir daha görmesin (kaydedince işaretlenir).
    const flagKey = `profile_done_${user.id}`;
    if (localStorage.getItem(flagKey)) return;

    let active = true;
    (async () => {
      const { data } = await (supabase.from("profiles") as any)
        .select("full_name, phone")
        .eq("id", user.id)
        .maybeSingle();
      if (!active) return;
      const pf = data as { full_name?: string | null; phone?: string | null } | null;
      const hasName = !!pf?.full_name?.trim();
      const hasPhone = !!pf?.phone?.trim();
      if (hasName && hasPhone) {
        localStorage.setItem(flagKey, "1"); // zaten tam → bir daha sorma
        return;
      }
      const meta = (user.user_metadata || {}) as Record<string, any>;
      setFullName(pf?.full_name?.trim() || meta.full_name || meta.name || "");
      setPhone(formatTrPhone(pf?.phone || meta.phone || ""));
      setOpen(true); // ZORUNLU — lead toplama için kapatılamaz
    })();
    return () => { active = false; };
  }, [user]);

  const save = async () => {
    if (!user) return;
    // Ad + SOYAD zorunlu: en az 2 kelime, her biri ≥2 harf (tek isim "ABDULAZİZ" geçmesin).
    const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
    if (nameParts.length < 2 || nameParts.some((p) => p.length < 2)) {
      toast.error("Lütfen ad ve soyadınızı tam girin (ör. Ahmet Yılmaz)"); return;
    }
    if (!isValidTrPhone(phone)) { toast.error("Geçerli bir cep telefonu girin (5XX XXX XX XX)"); return; }
    setSaving(true);
    // Mükerrer telefon engeli — Google kaydında telefon burada toplanıyor; aynı numarayla
    // birden çok hesap (bedava kredi farm'ı) açılmasın. E-posta akışındaki kontrolün eşdeğeri.
    try {
      const { data: taken } = await (supabase.rpc as any)("is_phone_taken", { p_phone: phoneDigits(phone) });
      if (taken === true) { toast.error("Bu telefon numarasıyla kayıtlı bir hesap zaten var."); setSaving(false); return; }
    } catch { /* RPC yoksa akışı bloklama */ }
    const { data, error } = await (supabase.from("profiles") as any)
      .update({ full_name: fullName.trim(), phone: phoneDigits(phone) })
      .eq("id", user.id)
      .select("id")
      .maybeSingle();
    if (error || !data) {
      toast.error("Kaydedilemedi, tekrar deneyin");
      console.error("profile save error:", error);
    } else {
      localStorage.setItem(`profile_done_${user.id}`, "1"); // kaydedildi → bir daha çıkmaz
      toast.success("Profilin tamamlandı!");
      setOpen(false);
    }
    setSaving(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-sm [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCheck className="h-5 w-5 text-primary" /> Profilini Tamamla</DialogTitle>
          <DialogDescription>Devam etmek için ad soyad ve telefon bilgini gir. (Bir kez)</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label>Ad Soyad *</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Ad ve Soyad (ör. Ahmet Yılmaz)" />
          </div>
          <div className="space-y-1.5">
            <Label>Telefon *</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground select-none">+90</span>
              <Input value={phone} onChange={(e) => setPhone(formatTrPhone(e.target.value))} placeholder="5XX XXX XX XX" type="tel" inputMode="numeric" maxLength={13} />
            </div>
          </div>
        </div>
        <div>
          <Button onClick={save} disabled={saving} className="w-full gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
            Kaydet ve Devam Et
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
